import React, { useEffect, useState } from 'react';
import { Step1Intro } from './Step1Intro';
import { Step2ThemeSelect } from './Step2ThemeSelect';
import { Step3Personalization } from './Step3Personalization';
import { Step4Preview } from './Step4Preview';
import { Step5Reveal } from './Step5Reveal';
import { WizardProgress } from '../WizardProgress';
import { ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { GenerationButtons } from '../../GenerationButtons';
interface ScenarioWizardProps {
  onComplete: (scenarioData: ScenarioData) => void;
  onBack: () => void;
  angelName?: string;
}
export interface ScenarioData {
  theme: string;
  location: string;
  outfit: string;
  tone: string;
  perspective: string;
  additionalDetails: string;
  premium: boolean;
  angelName?: string;
  angelId?: string;
  // New fields based on the UI screenshots
  sexActs?: string[];
  storyLength?: string;
  voice?: string;
  soundQuality?: string;
  includeSexSounds?: boolean;
}
export const ScenarioWizard: React.FC<ScenarioWizardProps> = ({
  onComplete,
  onBack,
  angelName
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showAngelSelector, setShowAngelSelector] = useState(!angelName);
  const [showMediaGeneration, setShowMediaGeneration] = useState(false);
  const [showImageGenerationModal, setShowImageGenerationModal] = useState(false);
  const [showVideoGenerationModal, setShowVideoGenerationModal] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [scenarioData, setScenarioData] = useState<ScenarioData>(() => {
    // Try to load saved progress from localStorage
    const savedData = localStorage.getItem('scenarioWizardProgress');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      // If we have an angel name from props, use it
      if (angelName) {
        return {
          ...parsedData,
          angelName
        };
      }
      return parsedData;
    }
    return {
      theme: '',
      location: '',
      outfit: '',
      tone: 'flirty',
      perspective: 'pov',
      additionalDetails: '',
      premium: false,
      angelName: angelName || '',
      angelId: ''
    };
  });
  // Sample angels data for selection
  const angels = [{
    id: '1',
    name: 'GoldenShowerTina',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bU73WvXeTZLjt1PKcu9KyE/image.png'
  }, {
    id: '2',
    name: 'Bonny Blue',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jq4Dkth7vgkmZJ8GZmzWDP/bonnyblue.png'
  }, {
    id: '3',
    name: 'Kim Holland',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/bZ3iVXv5nZEMY6F847weq5/kimholland.png'
  }, {
    id: '4',
    name: 'Mia Khalifa',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/gPbfjPdLfzNbqrZitYWXeS/MiaKhalifa.jpg'
  }, {
    id: '5',
    name: 'Harper',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/8g53dZ7J3kRR9dJNnGuDbc/h2.png'
  }, {
    id: '11',
    name: 'Lana',
    avatar: 'https://uploadthingy.s3.us-west-1.amazonaws.com/cLp9w3hC4dVahxHuCeYw1i/488653244_669275086051024_406085801635574977_n.jpg'
  }];
  // Save progress to localStorage whenever scenarioData changes
  useEffect(() => {
    localStorage.setItem('scenarioWizardProgress', JSON.stringify(scenarioData));
  }, [scenarioData]);
  // Update scenario data when angelName prop changes
  useEffect(() => {
    if (angelName) {
      setScenarioData(prev => ({
        ...prev,
        angelName
      }));
      setShowAngelSelector(false);
    }
  }, [angelName]);
  const totalSteps = 5;
  const goToNextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    } else {
      // Wizard completed
      onComplete(scenarioData);
    }
  };
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      // User wants to go back from first step
      onBack();
    }
  };
  const updateScenarioData = (data: Partial<ScenarioData>) => {
    setScenarioData(prev => ({
      ...prev,
      ...data
    }));
  };
  const selectAngel = (id: string, name: string) => {
    updateScenarioData({
      angelId: id,
      angelName: name
    });
    setShowAngelSelector(false);
  };
  const handleScenarioComplete = () => {
    setShowMediaGeneration(true);
  };
  const handleGenerateImage = () => {
    setShowImageGenerationModal(true);
  };
  const handleGenerateVideo = () => {
    setShowVideoGenerationModal(true);
  };
  const simulateImageGeneration = () => {
    setGenerationLoading(true);
    // Simulate API call with timeout
    setTimeout(() => {
      setGenerationLoading(false);
      setShowImageGenerationModal(false);
      // Show success notification or next steps here
    }, 3000);
  };
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Intro onNext={goToNextStep} onBack={goToPreviousStep} angelName={scenarioData.angelName} data-id="element-21815" />;
      case 2:
        return <Step2ThemeSelect selectedTheme={scenarioData.theme} onSelect={theme => updateScenarioData({
          theme
        })} onNext={goToNextStep} onBack={goToPreviousStep} angelName={scenarioData.angelName} data-id="element-21816" />;
      case 3:
        return <Step3Personalization scenarioData={scenarioData} onUpdate={updateScenarioData} onNext={goToNextStep} onBack={goToPreviousStep} data-id="element-21817" />;
      case 4:
        return <Step4Preview scenarioData={scenarioData} onNext={goToNextStep} onBack={goToPreviousStep} onUpdatePremium={premium => updateScenarioData({
          premium
        })} data-id="element-21818" />;
      case 5:
        return <Step5Reveal scenarioData={scenarioData} onComplete={handleScenarioComplete} onBack={goToPreviousStep} data-id="element-21819" />;
      default:
        return null;
    }
  };
  // Angel selection UI
  const renderAngelSelector = () => {
    return <div className="p-6" data-id="element-21820">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-21821">
          Choose Your Angel
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-6" data-id="element-21822">
          Select an angel to create a fantasy scenario with
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6" data-id="element-21823">
          {angels.map(angel => <div key={angel.id} onClick={() => selectAngel(angel.id, angel.name)} className={`p-3 border rounded-lg cursor-pointer transition-all flex items-center gap-3 ${scenarioData.angelId === angel.id ? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-pink-300'}`} data-id="element-21824">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0" data-id="element-21825">
                <img src={angel.avatar} alt={angel.name} className="w-full h-full object-cover" data-id="element-21826" />
              </div>
              <div className="flex-1" data-id="element-21827">
                <h3 className="font-medium text-gray-900 dark:text-white" data-id="element-21828">
                  {angel.name}
                </h3>
              </div>
              {scenarioData.angelId === angel.id && <div className="w-6 h-6 bg-pink-500 rounded-full flex items-center justify-center" data-id="element-21829">
                  <Check size={14} className="text-white" data-id="element-21830" />
                </div>}
            </div>)}
        </div>
        <div className="flex justify-between" data-id="element-21831">
          <button onClick={onBack} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center" data-id="element-21832">
            <ChevronLeft size={16} className="mr-1" data-id="element-21833" />
            <span data-id="element-21834">Back</span>
          </button>
          <button onClick={() => setShowAngelSelector(false)} disabled={!scenarioData.angelId} className={`px-4 py-2 rounded-lg flex items-center ${scenarioData.angelId ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white hover:from-pink-600 hover:to-purple-600 transition-all shadow-md shadow-pink-500/20' : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'}`} data-id="element-21835">
            <span data-id="element-21836">Continue</span>
            <ChevronRight size={16} className="ml-1" data-id="element-21837" />
          </button>
        </div>
      </div>;
  };
  return <div className="w-full max-w-4xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden" data-id="element-21838">
      <div className="p-6 bg-gradient-to-r from-pink-600 to-purple-600 text-white" data-id="element-21839">
        <h1 className="text-2xl font-bold" data-id="element-21840">Unleash Your Personal Fantasy</h1>
        <p className="text-pink-100" data-id="element-21841">
          {scenarioData.angelName ? `Create an exciting fantasy with ${scenarioData.angelName}` : 'Create your perfect fantasy in a few simple steps'}
        </p>
      </div>
      <WizardProgress currentStep={showAngelSelector ? 1 : currentStep} totalSteps={totalSteps} data-id="element-21842" />
      {showAngelSelector ? renderAngelSelector() : <div className="p-6" data-id="element-21843">
          {renderStep()}
          {showMediaGeneration && <div className="mt-8 p-4 bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-lg border border-pink-200 dark:border-pink-800/30" data-id="element-21844">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3" data-id="element-21845">
                Bring Your Fantasy to Life
              </h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-21846">
                Generate AI images or videos of your fantasy scenario with{' '}
                {scenarioData.angelName || 'your angel'}.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-21847">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-21848">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-21849">
                    Fantasy Image
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-21850">
                    Create a high-quality image of your scenario with{' '}
                    {scenarioData.angelName || 'your angel'}.
                  </p>
                  <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg mb-3 overflow-hidden" data-id="element-21851">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/bxeC2WeiFbrwJo5Qry9WCi/pasted-image.png" alt="Example fantasy image" className="w-full h-full object-cover opacity-80" data-id="element-21852" />
                  </div>
                  <button onClick={handleGenerateImage} className="w-full py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-all" data-id="element-21853">
                    Generate Image (10 💎)
                  </button>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-21854">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2" data-id="element-21855">
                    Fantasy Video
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-21856">
                    Create a 15-second video of your scenario with{' '}
                    {scenarioData.angelName || 'your angel'}.
                  </p>
                  <div className="aspect-video bg-gray-100 dark:bg-gray-700 rounded-lg mb-3 flex items-center justify-center" data-id="element-21857">
                    <div className="text-4xl" data-id="element-21858">🎬</div>
                  </div>
                  <button onClick={handleGenerateVideo} className="w-full py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-600 transition-all" data-id="element-21859">
                    Generate Video (25 💎)
                  </button>
                </div>
              </div>
              <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 text-center" data-id="element-21860">
                Premium content is generated using AI and requires DesireGems 💎
              </div>
            </div>}
          {/* Image Generation Modal */}
          {showImageGenerationModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300" data-id="element-21861">
              <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-xl overflow-hidden animate-in zoom-in-90 duration-300" data-id="element-21862">
                <div className="p-5 bg-gradient-to-r from-pink-600 to-purple-600 text-white" data-id="element-21863">
                  <h3 className="text-xl font-bold" data-id="element-21864">Generate Fantasy Image</h3>
                  <p className="text-pink-100 text-sm mt-1" data-id="element-21865">
                    Create an AI-generated image of your scenario with{' '}
                    {scenarioData.angelName || 'your angel'}
                  </p>
                </div>
                <div className="p-5" data-id="element-21866">
                  <div className="mb-4" data-id="element-21867">
                    <p className="text-gray-700 dark:text-gray-300 mb-2" data-id="element-21868">
                      Your image will be created based on:
                    </p>
                    <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-1 pl-5 list-disc" data-id="element-21869">
                      <li data-id="element-21870">
                        Theme:{' '}
                        <span className="font-medium text-gray-800 dark:text-gray-200 capitalize" data-id="element-21871">
                          {scenarioData.theme || 'Fantasy'}
                        </span>
                      </li>
                      <li data-id="element-21872">
                        Location:{' '}
                        <span className="font-medium text-gray-800 dark:text-gray-200 capitalize" data-id="element-21873">
                          {scenarioData.location || 'Bedroom'}
                        </span>
                      </li>
                      <li data-id="element-21874">
                        Outfit:{' '}
                        <span className="font-medium text-gray-800 dark:text-gray-200 capitalize" data-id="element-21875">
                          {scenarioData.outfit || 'Lingerie'}
                        </span>
                      </li>
                      <li data-id="element-21876">
                        Tone:{' '}
                        <span className="font-medium text-gray-800 dark:text-gray-200 capitalize" data-id="element-21877">
                          {scenarioData.tone || 'Flirty'}
                        </span>
                      </li>
                    </ul>
                  </div>
                  <div className="mb-4 p-3 bg-pink-50 dark:bg-pink-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30 flex items-center" data-id="element-21878">
                    <div className="mr-3 text-2xl" data-id="element-21879">💎</div>
                    <div data-id="element-21880">
                      <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-21881">
                        This will cost{' '}
                        <span className="font-bold text-pink-600 dark:text-pink-400" data-id="element-21882">
                          10 DesireGems
                        </span>
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-21883">
                        Your balance: 240 💎
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-3 mt-5" data-id="element-21884">
                    <button onClick={() => setShowImageGenerationModal(false)} className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-21885">
                      Cancel
                    </button>
                    <button onClick={simulateImageGeneration} disabled={generationLoading} className="flex-1 py-2.5 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg font-medium hover:from-pink-600 hover:to-purple-600 transition-all flex items-center justify-center" data-id="element-21886">
                      {generationLoading ? <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-21887">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-21888"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-21889"></path>
                          </svg>
                          Generating...
                        </> : 'Generate Now'}
                    </button>
                  </div>
                </div>
              </div>
            </div>}
        </div>}
    </div>;
};